/**
 * เอฟเฟกต์ภาพของการโจมตีและสกิล
 *
 * เป็นงานฝั่งภาพล้วน ไม่มีผลต่อเกม ทุกอย่างที่เห็นเกิดจาก event ที่ server
 * ส่งมาแล้ว (`hit`, `skillCast`, `levelUp`, `warp`) ไม่ได้คิดเองว่าใครโดนอะไร
 *
 * ใช้การหมุนเวียน mesh กลับมาใช้ซ้ำ (pool) แทนการสร้างและทิ้งทุกครั้ง
 * เพื่อประสิทธิภาพสูงสุดและความลื่นไหลของฉาก
 */

import type { Scene } from "@babylonjs/core/scene";
import { Color3, Vector3 } from "@babylonjs/core/Maths/math";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Constants } from "@babylonjs/core/Engines/constants";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import "@babylonjs/core/Particles/particleSystemComponent";
import "@babylonjs/core/Shaders/particles.vertex";
import "@babylonjs/core/Shaders/particles.fragment";
import { fxTexture } from "./fx-textures.js";

/** ชิ้นส่วนที่กำลังแสดงอยู่ */
interface Live {
  mesh: Mesh;
  mat: StandardMaterial;
  /** เวลาที่ผ่านไปแล้ว (วินาที) */
  age: number;
  /** อายุรวม (วินาที) */
  life: number;
  kind: "sphere" | "torus" | "cylinder" | "spark" | "circle";
  /** ความเร็วหมุน (วงเวท) */
  spin?: number;
  /** ลูกศรหรือกระสุน: เดินทางจากต้นทางไปปลายทาง */
  from?: Vector3;
  to?: Vector3;
  /** การขยายขนาดรัศมี */
  r0?: number;
  r1?: number;
  /** ความสูงเริ่มต้น/สิ้นสุด */
  h0?: number;
  h1?: number;
  /** ความเร็วลอยตัว */
  vy?: number;
  onArrive?: () => void;
}

export interface EffectSystem {
  /** กระสุนหรือลูกธนูวิ่งจากผู้ร่ายไปเป้าหมาย ถึงแล้วเรียก onArrive */
  projectile(from: Vector3, to: Vector3, hex: string, speed: number, onArrive?: () => void): void;
  /** แสงกระจายตรงจุดที่โดน */
  burst(at: Vector3, hex: string, size?: number): void;
  /** วงแหวนแผ่บนพื้น ใช้กับสกิลวงกว้าง */
  groundRing(x: number, z: number, radius: number, hex: string): void;
  /** วงแหวนลอยขึ้นรอบตัว ใช้ตอนได้บัฟ */
  buffRing(at: Vector3, hex: string): void;
  /** เสาแสงศักดิ์สิทธิ์หรือลำแสงเวทมนตร์พุ่งขึ้นจากฟ้า/พื้น */
  pillar(at: Vector3, hex: string, height?: number, radius?: number): void;
  /** คลื่นฟันรูปเสี้ยวจันทร์ (Slash Arc) */
  slashArc(at: Vector3, hex: string, rotY?: number): void;
  /** แท่งผลึกน้ำแข็งพุ่งขึ้นจากดิน */
  iceSpikes(at: Vector3, hex?: string): void;
  /** ประกายละอองเวทมนตร์หรือละอองฟื้นฟู HP ลอยวนขึ้น */
  healSparks(at: Vector3, hex?: string): void;
  /** อุกกาบาตพุ่งตกลงมาจากฟ้าแล้วระเบิด */
  meteorStrike(to: Vector3, hex?: string): void;
  /** สายฟ้าฟาดลงมาจากท้องฟ้า */
  lightningBolt(from: Vector3, to: Vector3, hex?: string): void;
  /** วงเวทวาร์ปแสงดวงดาว */
  warpFx(at: Vector3, hex?: string): void;
  /** วงเวทอักขระบนพื้น หมุนช้า (สกิลวงกว้าง/บัฟ/รักษา) — รอบ 20 */
  magicCircle(x: number, z: number, radius: number, hex: string, life?: number): void;
  /** ประกายแตกกระจาย (อนุภาค) ตอนโดน */
  sparks(at: Vector3, hex: string, count?: number, power?: number): void;
  /** ควันฟุ้ง (ตีหนัก/คริ) */
  smoke(at: Vector3, hex?: string, count?: number): void;
  /** ลำแสงไล่จางจากโคนขึ้นยอด (รักษา/บัฟ) */
  beam(at: Vector3, hex: string, height?: number, radius?: number): void;
  update(dt: number): void;
}

/** ความหนาแน่นอนุภาค 1 = กราฟิกสูง · 0.5 = ต่ำ (ไม่มีควัน) — main.ts ตั้งตาม settings.hiGraphics */
let quality = 1;
export function setFxQuality(q: number): void {
  quality = q;
}

export function createEffects(scene: Scene): EffectSystem {
  const live: Live[] = [];
  const freeSpheres: Mesh[] = [];
  const freeTori: Mesh[] = [];
  const freeCylinders: Mesh[] = [];
  let serial = 0;

  /** จำกัดจำนวนที่แสดงพร้อมกัน กันเฟรมตกตอนตีกันรัว ๆ */
  const MAX_LIVE = 160;
  const freePlanes: Mesh[] = [];
  /** อนุภาคใช้ระบบหมุนเวียน (สร้าง ParticleSystem ใหม่ทุกครั้งแพง) — ว่างเมื่อ freeAt ผ่านไปแล้ว */
  const psPool: { ps: ParticleSystem; freeAt: number; tex: "glow" | "smoke" }[] = [];
  let clock = 0;

  function takePlane(): Mesh {
    const m = freePlanes.pop();
    if (m) return m;
    const mesh = MeshBuilder.CreateGround(`fx-p${serial++}`, { width: 1, height: 1 }, scene);
    mesh.isPickable = false;
    return mesh;
  }

  /** วัสดุเรืองแบบบวกแสง (สีดำ = โปร่ง) ใช้ texture ร่วม — ทิ้งวัสดุแล้ว texture ยังอยู่ */
  function makeAddMat(hex: string, tex: ReturnType<typeof fxTexture>): StandardMaterial {
    const mat = new StandardMaterial(`fx-a${serial++}`, scene);
    mat.disableLighting = true;
    // texture ย้อมสีมาแล้ว (fxTexture tint) ใช้เป็นแสงเรืองตรง ๆ · ไม่มี texture = สีเรียบ
    mat.diffuseColor = Color3.Black();
    mat.specularColor = Color3.Black();
    mat.emissiveColor = tex ? Color3.Black() : Color3.FromHexString(hex);
    if (tex) {
      mat.emissiveTexture = tex;
      mat.opacityTexture = tex;
    }
    mat.alphaMode = Constants.ALPHA_ADD;
    mat.backFaceCulling = false;
    mat.disableDepthWrite = true;
    return mat;
  }

  function takePs(tex: "glow" | "smoke"): ParticleSystem | null {
    const t = fxTexture(scene, tex);
    if (!t) return null;
    let slot = psPool.find((p) => p.tex === tex && p.freeAt <= clock);
    if (!slot) {
      if (psPool.length >= 24) return null;
      const ps = new ParticleSystem(`fx-ps${serial++}`, 80, scene);
      ps.particleTexture = t;
      ps.blendMode = tex === "glow" ? ParticleSystem.BLENDMODE_ADD : ParticleSystem.BLENDMODE_STANDARD;
      ps.emitRate = 0;
      ps.minEmitBox = new Vector3(-0.5, 0, -0.5);
      ps.maxEmitBox = new Vector3(0.5, 0.6, 0.5);
      slot = { ps, freeAt: 0, tex };
      psPool.push(slot);
    }
    slot.freeAt = clock + 1.6;
    return slot.ps;
  }

  function takeSphere(): Mesh {
    const m = freeSpheres.pop();
    if (m) return m;
    const mesh = MeshBuilder.CreateSphere(`fx-s${serial++}`, { diameter: 1, segments: 6 }, scene);
    mesh.isPickable = false;
    return mesh;
  }

  function takeTorus(): Mesh {
    const m = freeTori.pop();
    if (m) return m;
    const mesh = MeshBuilder.CreateTorus(
      `fx-t${serial++}`,
      { diameter: 1, thickness: 0.09, tessellation: 20 },
      scene,
    );
    mesh.isPickable = false;
    return mesh;
  }

  function takeCylinder(): Mesh {
    const m = freeCylinders.pop();
    if (m) return m;
    const mesh = MeshBuilder.CreateCylinder(
      `fx-c${serial++}`,
      { diameter: 1, height: 1, tessellation: 12 },
      scene,
    );
    mesh.isPickable = false;
    return mesh;
  }

  /** วัสดุเรืองแสงโปร่งใส หนึ่งชิ้นต่อหนึ่งเอฟเฟกต์ เพราะต้องลดความทึบแยกกัน */
  function makeMat(hex: string): StandardMaterial {
    const mat = new StandardMaterial(`fx-m${serial++}`, scene);
    const c = Color3.FromHexString(hex);
    mat.diffuseColor = c;
    mat.emissiveColor = c.scale(0.9);
    mat.specularColor = Color3.Black();
    mat.alpha = 1;
    mat.disableLighting = true;
    mat.backFaceCulling = false;
    return mat;
  }

  function push(e: Live): void {
    if (live.length >= MAX_LIVE) {
      const oldest = live.shift();
      if (oldest) retire(oldest);
    }
    live.push(e);
  }

  function retire(e: Live): void {
    e.mesh.setEnabled(false);
    e.mat.dispose(false, false);
    e.mesh.material = null;
    if (e.kind === "circle") {
      freePlanes.push(e.mesh);
    } else if (e.kind === "sphere" || e.kind === "spark") {
      freeSpheres.push(e.mesh);
    } else if (e.kind === "torus") {
      freeTori.push(e.mesh);
    } else {
      freeCylinders.push(e.mesh);
    }
  }

  const sys: EffectSystem = {
    projectile(from, to, hex, speed, onArrive) {
      const mesh = takeSphere();
      const mat = makeMat(hex);
      mesh.material = mat;
      mesh.scaling.setAll(0.42);
      mesh.position.copyFrom(from);
      mesh.setEnabled(true);
      const dist = Vector3.Distance(from, to);
      push({
        mesh,
        mat,
        age: 0,
        life: Math.max(0.08, dist / speed),
        kind: "sphere",
        from: from.clone(),
        to: to.clone(),
        ...(onArrive ? { onArrive } : {}),
      });
    },

    burst(at, hex, size = 1.6) {
      const mesh = takeTorus();
      const mat = makeMat(hex);
      mesh.material = mat;
      mesh.position.copyFrom(at);
      mesh.rotation.set(Math.PI / 2, 0, 0);
      mesh.setEnabled(true);
      push({ mesh, mat, age: 0, life: 0.32, kind: "torus", r0: 0.3, r1: size });
    },

    groundRing(x, z, radius, hex) {
      const mesh = takeTorus();
      const mat = makeMat(hex);
      mesh.material = mat;
      mesh.position.set(x, 0.12, z);
      mesh.rotation.set(0, 0, 0);
      mesh.setEnabled(true);
      push({ mesh, mat, age: 0, life: 0.55, kind: "torus", r0: 0.4, r1: radius * 2 });
    },

    buffRing(at, hex) {
      const mesh = takeTorus();
      const mat = makeMat(hex);
      mesh.material = mat;
      mesh.position.copyFrom(at);
      mesh.position.y = 0.15;
      mesh.rotation.set(0, 0, 0);
      mesh.setEnabled(true);
      push({ mesh, mat, age: 0, life: 0.9, kind: "torus", r0: 2.2, r1: 1.2, vy: 1.6 });
    },

    pillar(at, hex, height = 7, radius = 1.4) {
      const mesh = takeCylinder();
      const mat = makeMat(hex);
      mat.alpha = 0.85;
      mesh.material = mat;
      mesh.position.set(at.x, at.y + height / 2, at.z);
      mesh.scaling.set(radius, height, radius);
      mesh.rotation.set(0, 0, 0);
      mesh.setEnabled(true);
      push({ mesh, mat, age: 0, life: 0.65, kind: "cylinder", r0: radius, r1: radius * 1.3, h0: height });
    },

    slashArc(at, hex, rotY = 0) {
      const mesh = takeTorus();
      const mat = makeMat(hex);
      mesh.material = mat;
      mesh.position.copyFrom(at);
      mesh.position.y += 0.8;
      mesh.rotation.set(Math.PI / 3, rotY, 0);
      mesh.setEnabled(true);
      push({ mesh, mat, age: 0, life: 0.22, kind: "torus", r0: 0.6, r1: 2.4 });
    },

    iceSpikes(at, hex = "#67e8f9") {
      for (let i = 0; i < 4; i++) {
        const mesh = takeCylinder();
        const mat = makeMat(hex);
        mesh.material = mat;
        const angle = (i * Math.PI) / 2 + Math.random() * 0.4;
        const dist = 0.5 + Math.random() * 0.6;
        mesh.position.set(at.x + Math.cos(angle) * dist, at.y + 0.8, at.z + Math.sin(angle) * dist);
        mesh.rotation.set((Math.random() - 0.5) * 0.4, angle, (Math.random() - 0.5) * 0.4);
        mesh.scaling.set(0.3, 1.6, 0.3);
        mesh.setEnabled(true);
        push({ mesh, mat, age: 0, life: 0.7, kind: "cylinder", r0: 0.3, r1: 0.05, h0: 1.6 });
      }
    },

    healSparks(at, hex = "#4ade80") {
      for (let i = 0; i < 6; i++) {
        const mesh = takeSphere();
        const mat = makeMat(hex);
        mesh.material = mat;
        const a = (i * Math.PI) / 3;
        mesh.position.set(at.x + Math.cos(a) * 0.8, at.y + 0.2 + i * 0.2, at.z + Math.sin(a) * 0.8);
        mesh.scaling.setAll(0.2);
        mesh.setEnabled(true);
        push({
          mesh,
          mat,
          age: 0,
          life: 0.75,
          kind: "spark",
          r0: 0.2,
          r1: 0.05,
          vy: 2.2 + Math.random() * 0.8,
        });
      }
    },

    meteorStrike(to, hex = "#f97316") {
      const from = new Vector3(to.x + 8, to.y + 16, to.z + 8);
      sys.projectile(from, to, hex, 32, () => {
        sys.burst(to, hex, 3.8);
        sys.groundRing(to.x, to.z, 3.5, hex);
        sys.pillar(to, "#ffedd5", 5, 2.2);
      });
    },

    lightningBolt(from, to, hex = "#facc15") {
      const mesh = takeCylinder();
      const mat = makeMat(hex);
      mesh.material = mat;
      const mid = Vector3.Center(from, to);
      mesh.position.copyFrom(mid);
      const h = Vector3.Distance(from, to);
      mesh.scaling.set(0.25, h, 0.25);
      mesh.lookAt(to);
      mesh.rotate(new Vector3(1, 0, 0), Math.PI / 2);
      mesh.setEnabled(true);
      push({ mesh, mat, age: 0, life: 0.25, kind: "cylinder", r0: 0.25, r1: 0.5 });
      sys.burst(to, hex, 2.4);
    },

    warpFx(at, hex = "#38bdf8") {
      sys.buffRing(at, hex);
      sys.pillar(at, hex, 6, 1.2);
      for (let i = 0; i < 5; i++) {
        const mesh = takeSphere();
        const mat = makeMat(hex);
        mesh.material = mat;
        mesh.position.set(at.x + (Math.random() - 0.5) * 1.4, at.y + 0.2, at.z + (Math.random() - 0.5) * 1.4);
        mesh.scaling.setAll(0.25);
        mesh.setEnabled(true);
        push({ mesh, mat, age: 0, life: 0.8, kind: "spark", r0: 0.25, r1: 0.02, vy: 3.5 });
      }
    },

    magicCircle(x, z, radius, hex, life = 0.9) {
      const tex = fxTexture(scene, "circle", hex);
      if (!tex) return;
      const mesh = takePlane();
      const mat = makeAddMat(hex, tex);
      mesh.material = mat;
      mesh.position.set(x, 0.06 + Math.random() * 0.02, z);
      mesh.rotation.set(0, Math.random() * 6, 0);
      mesh.scaling.setAll(radius * 1.5);
      mesh.setEnabled(true);
      push({ mesh, mat, age: 0, life, kind: "circle", r0: radius * 1.5, r1: radius * 2.15, spin: 1.2 });
    },

    sparks(at, hex, count = 18, power = 1) {
      const ps = takePs("glow");
      if (!ps) return;
      const c = Color3.FromHexString(hex);
      ps.emitter = at.clone();
      ps.color1 = new Color4(c.r, c.g, c.b, 1);
      ps.color2 = new Color4(Math.min(1, c.r + 0.4), Math.min(1, c.g + 0.4), Math.min(1, c.b + 0.4), 1);
      ps.colorDead = new Color4(c.r * 0.3, c.g * 0.3, c.b * 0.3, 0);
      ps.minSize = 0.18 * power;
      ps.maxSize = 0.5 * power;
      ps.minLifeTime = 0.25;
      ps.maxLifeTime = 0.6;
      ps.minEmitPower = 3 * power;
      ps.maxEmitPower = 7 * power;
      ps.direction1 = new Vector3(-1, 0.4, -1);
      ps.direction2 = new Vector3(1, 1.6, 1);
      ps.gravity = new Vector3(0, -9, 0);
      ps.manualEmitCount = Math.round(count * quality);
      ps.start();
    },

    smoke(at, hex = "#f2efe8", count = 8) {
      if (quality < 1) return;
      const ps = takePs("smoke");
      if (!ps) return;
      const c = Color3.FromHexString(hex);
      ps.emitter = at.clone();
      ps.color1 = new Color4(c.r, c.g, c.b, 0.5);
      ps.color2 = new Color4(c.r * 0.9, c.g * 0.9, c.b * 0.9, 0.35);
      ps.colorDead = new Color4(c.r, c.g, c.b, 0);
      ps.minSize = 0.8;
      ps.maxSize = 1.7;
      ps.minLifeTime = 0.45;
      ps.maxLifeTime = 0.9;
      ps.minEmitPower = 1;
      ps.maxEmitPower = 2.4;
      ps.direction1 = new Vector3(-1, 0.3, -1);
      ps.direction2 = new Vector3(1, 1, 1);
      ps.gravity = new Vector3(0, 0.6, 0);
      ps.minAngularSpeed = -1.5;
      ps.maxAngularSpeed = 1.5;
      ps.manualEmitCount = count;
      ps.start();
    },

    beam(at, hex, height = 8, radius = 1.2) {
      const tex = fxTexture(scene, "beam", hex);
      if (!tex) {
        sys.pillar(at, hex, height, radius);
        return;
      }
      const mesh = takeCylinder();
      const mat = makeAddMat(hex, tex);
      mesh.material = mat;
      mesh.position.set(at.x, height / 2, at.z);
      mesh.scaling.set(radius, height, radius);
      mesh.rotation.set(0, 0, 0);
      mesh.setEnabled(true);
      push({ mesh, mat, age: 0, life: 0.8, kind: "cylinder", r0: radius * 0.4, r1: radius * 1.4, h0: height });
    },

    update(dt) {
      clock += dt;
      for (let i = live.length - 1; i >= 0; i--) {
        const e = live[i]!;
        e.age += dt;
        const t = Math.min(1, e.age / e.life);

        if (e.kind === "sphere" && e.from && e.to) {
          Vector3.LerpToRef(e.from, e.to, t, e.mesh.position);
          e.mesh.position.y += Math.sin(t * Math.PI) * 0.7;
          e.mat.alpha = 1;
        } else if (e.kind === "spark") {
          if (e.vy) e.mesh.position.y += e.vy * dt;
          const r = (e.r0 ?? 0.2) + ((e.r1 ?? 0.05) - (e.r0 ?? 0.2)) * t;
          e.mesh.scaling.setAll(Math.max(0.01, r));
          e.mat.alpha = 1 - t;
        } else if (e.kind === "circle") {
          // โผล่เร็ว (ขยายใน 15% แรก) แล้วหมุนช้า ๆ จางตอนท้าย
          const grow = Math.min(1, t / 0.15);
          const r = (e.r0 ?? 1) + ((e.r1 ?? 1) - (e.r0 ?? 1)) * grow;
          e.mesh.scaling.set(r, 1, r);
          e.mesh.rotation.y += (e.spin ?? 1) * dt;
          e.mat.alpha = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
        } else if (e.kind === "cylinder") {
          const r = (e.r0 ?? 1) + ((e.r1 ?? 1) - (e.r0 ?? 1)) * t;
          e.mesh.scaling.x = r;
          e.mesh.scaling.z = r;
          if (e.h0) e.mesh.scaling.y = e.h0;
          e.mat.alpha = (1 - t) * 0.9;
        } else {
          // torus
          const r = (e.r0 ?? 0) + ((e.r1 ?? 1) - (e.r0 ?? 0)) * t;
          e.mesh.scaling.setAll(r);
          if (e.vy) e.mesh.position.y += e.vy * dt;
          e.mat.alpha = 1 - t;
        }

        if (t >= 1) {
          e.onArrive?.();
          retire(e);
          live.splice(i, 1);
        }
      }
    },
  };

  return sys;
}
